import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Badge, Alert, Modal } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import PackageService, { Package, PaymentDetails } from "../services/PackageService";
import { fetchMyGroups } from "../services/UserGroupService";
import { apiGet } from "../services/BaseService";
import { useSelector } from "react-redux";

const PackageSelectionPage: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [customScheduleCount, setCustomScheduleCount] = useState(1);
  const [customTicketCount, setCustomTicketCount] = useState(50);
  const [customPrice, setCustomPrice] = useState(0);
  const [customPriceBreakdown, setCustomPriceBreakdown] = useState<any>(null);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'netbanking'>('upi');
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes
  const [timerActive, setTimerActive] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'expired'>('pending');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [countries, setCountries] = useState<{countryCode: string, name: string}[]>([]);
  const [states, setStates] = useState<{stateCode: string, name: string}[]>([]);
  const [groupFilters, setGroupFilters] = useState({
    country: '',
    state: ''
  });

  const { user } = useSelector((state: any) => state.auth);
  
  const isSuperAdmin = (user?.user ? user?.user?.userRoleCode: user?.userRoleCode) === 'ROLE_SUPER_ADMIN' ;

useEffect(() => {
    fetchPackages();
    fetchPaymentDetails();
    if (isSuperAdmin) {
      fetchCountries();
    }
    fetchUserGroups();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && groupFilters.country) {
      fetchStates(groupFilters.country);
      fetchFilteredGroups(groupFilters.country);
    } else if (isSuperAdmin) {
      setStates([]);
      fetchAllGroups();
    }
  }, [groupFilters.country, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && groupFilters.country && groupFilters.state) {
      fetchFilteredGroups(groupFilters.country, groupFilters.state);
    } else if (isSuperAdmin && groupFilters.country) {
      fetchFilteredGroups(groupFilters.country);
    }
  }, [groupFilters.state, isSuperAdmin]);

  useEffect(() => {
    if (userGroups.length === 1) {
      setSelectedGroupId(userGroups[0]._id);
    }
  }, [userGroups]);

  useEffect(() => {
    if (showCustomBuilder) {
      calculateCustomPrice();
    }
  }, [customScheduleCount, customTicketCount, showCustomBuilder]);

  const fetchPackages = async () => {
    try {
      const data = await PackageService.getPackages();
      setPackages(data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const data = await PackageService.getPaymentDetails();
      setPaymentDetails(data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const data = await fetchMyGroups(1, 100);
      setUserGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await PackageService.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryCode: string) => {
    try {
      const data = await PackageService.getStates(countryCode);
      setStates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchFilteredGroups = async (countryCode?: string, stateCode?: string) => {
    try {
      let url = '/groups';
      const params = new URLSearchParams();
      if (countryCode) params.append('country', countryCode);
      if (stateCode) params.append('state', stateCode);
      
      if (params.toString()) {
        url = `/groups/filter?${params.toString()}`;
      }
      
      const data = await apiGet(url);
      setUserGroups(Array.isArray(data.groups || data) ? (data.groups || data) : []);
    } catch (error) {
      console.error('Error fetching filtered groups:', error);
    }
  };

  const fetchAllGroups = async () => {
    try {
      const data = await PackageService.getGroups();
      setUserGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching all groups:', error);
    }
  };

  const calculateCustomPrice = async () => {
    try {
      const priceData = await PackageService.calculatePrice(customScheduleCount, customTicketCount);
      setCustomPriceBreakdown(priceData);
      setCustomPrice(priceData.discount?.finalPrice || priceData.totalPrice);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const handlePackageSelect = async (pkg: Package) => {
    setSelectedPackage(pkg);
    generateQRCode(pkg.totalPrice);
    setTimeLeft(180);
    setTimerActive(true);
    setPaymentStatus('pending');
    setShowPaymentModal(true);
  };

  const handleCustomPackageCreate = async () => {
    try {
      setLoading(true);
      const customPackage = await PackageService.createCustomPackage(customScheduleCount, customTicketCount);
      setSelectedPackage(customPackage);
      generateQRCode(customPackage.totalPrice);
      setTimeLeft(180);
      setTimerActive(true);
      setPaymentStatus('pending');
      setShowCustomBuilder(false);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error creating custom package:', error);
    } finally {
      setLoading(false);
    }
  };

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            setPaymentStatus('expired');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const submitPayment = async () => {
    if (!selectedPackage || !transactionId.trim()) {
      alert('Please enter transaction ID');
      return;
    }
    
    if (!selectedGroupId) {
      alert('Please select a group');
      return;
    }
    
    try {
      setLoading(true);
      await PackageService.submitPaymentRequest({
        packageId: selectedPackage._id,
        groupId: selectedGroupId,
        amount: selectedPackage.totalPrice,
        paymentMethod: paymentMethod as 'upi' | 'netbanking',
        transactionId: transactionId.trim()
      });
      
      setPaymentStatus('paid');
      setTimerActive(false);
      alert('Payment request submitted successfully! Admin will verify and approve.');
    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('Error submitting payment request');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = (amount: number) => {
    if (!paymentDetails?.upiId) return;
    const upiId = paymentDetails.upiId;
    const upiName = paymentDetails.accountHolderName || "Natakiya";
    const note = `Package Payment - ${selectedPackage?.packageName || 'Custom Package'}`;
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiUrl)}`;
    setQrCodeUrl(qrUrl);
  };

  const generateUPILink = (app: 'gpay' | 'phonepe') => {
    if (!selectedPackage || !paymentDetails?.upiId) return "";
    const amount = selectedPackage.totalPrice;
    const upiId = paymentDetails.upiId;
    const upiName = paymentDetails.accountHolderName || "Natakiya";
    const note = `Package Payment - ${selectedPackage.packageName}`;
    
    if (app === 'gpay') {
      return `tez://upi/pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
    } else {
      return `phonepe://pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
    }
  };

  const handlePayLater = async () => {
    if (!selectedPackage) return;
    
    if (!selectedGroupId) {
      alert('Please select a group');
      return;
    }
    
    try {
      setLoading(true);
      await PackageService.submitPaymentRequest({
        packageId: selectedPackage._id,
        groupId: selectedGroupId,
        amount: selectedPackage.totalPrice,
        paymentMethod: paymentMethod as 'upi' | 'netbanking',
        transactionId: '',
        status: 'pending'
      });
      
      alert('Payment request saved. You can complete payment later from your dashboard.');
      setShowPaymentModal(false);
      setTimerActive(false);
    } catch (error) {
      console.error('Error saving payment request:', error);
      alert('Error saving payment request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionWrapper pageName="PackageSelectionPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-box-seam me-2"></i>
              Select Your Package
            </h2>
            <p className="text-muted">Choose from our predefined packages or create a custom one</p>
          </Col>
        </Row>

        {/* Group Selection */}
        <Row className="mb-4">
          <Col>
            <Card className="border-warning">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="bi bi-people me-2"></i>
                  Select Group
                </h5>
              </Card.Header>
              <Card.Body>
                {isSuperAdmin ? (
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Select 
                          value={groupFilters.country} 
                          onChange={(e) => {
                            const country = e.target.value;
                            setGroupFilters({country, state: ''});
                            setSelectedGroupId('');
                          }}
                        >
                          <option value="">All Countries</option>
                          {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <Form.Select 
                          value={groupFilters.state} 
                          onChange={(e) => {
                            setGroupFilters({...groupFilters, state: e.target.value});
                            setSelectedGroupId('');
                          }}
                          disabled={!groupFilters.country}
                        >
                          <option value="">All States</option>
                          {states.map(state => (
                            <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Group *</Form.Label>
                        <Form.Select
                          value={selectedGroupId}
                          onChange={(e) => setSelectedGroupId(e.target.value)}
                          required
                        >
                          <option value="">Select a group...</option>
                          {userGroups.map((group) => (
                            <option key={group._id} value={group._id}>
                              {group.name} - {group.groupCode}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Choose the group for this package *</Form.Label>
                    <Form.Select
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                      required
                    >
                      <option value="">Select a group...</option>
                      {userGroups.map((group) => (
                        <option key={group._id} value={group._id}>
                          {group.name} - {group.groupCode}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Text className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Package credits will be allocated to the selected group.
                  {isSuperAdmin && ' Use filters to find groups easily.'}
                </Form.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {userGroups.length === 0 && (
          <Row className="mb-4">
            <Col>
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>No Groups Found:</strong> 
                {isSuperAdmin ? (
                  groupFilters.country || groupFilters.state ? ' No groups found for selected filters.' : ' No groups available in the system.'
                ) : (
                  ' You need to be a member of at least one group to purchase packages. Please contact admin or join a group first.'
                )}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Custom Package Builder */}
        <Row className="mb-4">
          <Col>
            <Card className="border-primary">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="bi bi-sliders me-2"></i>
                  Create Custom Package
                </h5>
              </Card.Header>
              <Card.Body>
                {!showCustomBuilder ? (
                  <div className="text-center">
                    <p>Need a custom package? Build your own with our flexible pricing.</p>
                    <Button variant="primary" onClick={() => setShowCustomBuilder(true)}>
                      <i className="bi bi-plus-circle me-2"></i>
                      Build Custom Package
                    </Button>
                  </div>
                ) : (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Schedules: {customScheduleCount}</Form.Label>
                        <Form.Range
                          min={1}
                          max={50}
                          value={customScheduleCount}
                          onChange={(e) => setCustomScheduleCount(parseInt(e.target.value))}
                        />
                        <small className="text-muted">Minimum: 1 schedule</small>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tickets per Schedule: {customTicketCount}</Form.Label>
                        <Form.Range
                          min={50}
                          max={1000}
                          step={10}
                          value={customTicketCount}
                          onChange={(e) => setCustomTicketCount(parseInt(e.target.value))}
                        />
                        <small className="text-muted">Minimum: 50 tickets per schedule</small>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="text-primary mb-0">Total Price: ₹{customPrice}</h5>
                          {customPriceBreakdown?.discount?.discountPercent > 0 && (
                            <div className="mb-2">
                              <small className="text-success">
                                <i className="bi bi-tag me-1"></i>
                                {customPriceBreakdown.discount.discountReason}: {customPriceBreakdown.discount.discountPercent}% off
                              </small>
                              <br/>
                              <small className="text-muted">
                                Original: ₹{customPriceBreakdown.discount.originalPrice} → Final: ₹{customPriceBreakdown.discount.finalPrice}
                              </small>
                            </div>
                          )}
                          <small className="text-muted">
                            {customScheduleCount} schedules × {customTicketCount} tickets each
                          </small>
                        </div>
                        <div>
                          <Button 
                            variant="outline-secondary" 
                            className="me-2"
                            onClick={() => setShowCustomBuilder(false)}
                          >
                            Cancel
                          </Button>
                          <Button 
                            variant="primary" 
                            onClick={handleCustomPackageCreate}
                            disabled={loading || !selectedGroupId || userGroups.length === 0}
                          >
                            {loading ? 'Creating...' : 
                             !selectedGroupId && userGroups.length > 1 ? 'Select Group First' :
                             userGroups.length === 0 ? 'No Groups Available' :
                             'Create & Select'}
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Predefined Packages */}
        <Row className="mb-4">
          <Col>
            <h4>Predefined Packages</h4>
          </Col>
        </Row>

        <Row className="g-4">
          {packages.filter(pkg => !pkg.isRestricted).map((pkg) => (
            <Col key={pkg._id} xs={12} md={6} lg={4}>
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{pkg.packageName}</h5>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <span>Schedules:</span>
                      <strong>{pkg.scheduleCount}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Tickets per Schedule:</span>
                      <strong>{pkg.ticketCountPerSchedule}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Total Tickets:</span>
                      <strong>{pkg.scheduleCount * pkg.ticketCountPerSchedule}</strong>
                    </div>
                  </div>
                  <hr />
                  <div className="text-center">
                    <h4 className="text-primary">₹{pkg.totalPrice}</h4>
                    <Button 
                      variant="primary" 
                      className="w-100"
                      onClick={() => handlePackageSelect(pkg)}
                      disabled={!selectedGroupId || userGroups.length === 0}
                    >
                      {!selectedGroupId && userGroups.length > 1 ? 'Select Group First' :
                       userGroups.length === 0 ? 'No Groups Available' :
                       'Select Package'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {packages.filter(pkg => !pkg.isRestricted).length === 0 && userGroups.length > 0 && (
          <Row>
            <Col>
              <Alert variant="info" className="text-center">
                <i className="bi bi-info-circle me-2"></i>
                No packages available at the moment. Please create a custom package or contact administrator.
              </Alert>
            </Col>
          </Row>
        )}

        {/* Payment Modal */}
        <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg" centered>
          <Modal.Header closeButton style={{ backgroundColor: '#810081', color: 'white' }}>
            <Modal.Title>
              <i className="bi bi-credit-card me-2"></i>
              Complete Payment
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedPackage && (
              <div>
                {/* Package Details */}
                <Card className="mb-4 border-0" style={{ backgroundColor: 'rgba(129, 0, 129, 0.1)' }}>
                  <Card.Body>
                    <h6 style={{ color: '#810081' }}>{selectedPackage.packageName}</h6>
                    {selectedGroupId && (
                      <div className="mb-2">
                        <small className="text-muted">
                          <i className="bi bi-people me-1"></i>
                          <strong>Group:</strong> {userGroups.find(g => g._id === selectedGroupId)?.name}
                        </small>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-3 mt-2">
                      <small><i className="bi bi-calendar me-1"></i><strong>Schedules:</strong> {selectedPackage.scheduleCount}</small>
                      <small><i className="bi bi-ticket me-1"></i><strong>Tickets/Schedule:</strong> {selectedPackage.ticketCountPerSchedule}</small>
                      <small><i className="bi bi-collection me-1"></i><strong>Total Tickets:</strong> {selectedPackage.scheduleCount * selectedPackage.ticketCountPerSchedule}</small>
                    </div>
                    <div className="mt-3">
                      <h5 style={{ color: '#FFD600', backgroundColor: '#810081', padding: '8px 16px', borderRadius: '8px', display: 'inline-block' }}>
                        Amount: ₹{selectedPackage.totalPrice}
                      </h5>
                    </div>
                  </Card.Body>
                </Card>

                {/* Payment Method Selection */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Payment Method</Form.Label>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      label={<span><i className="bi bi-phone me-2"></i>UPI Payment</span>}
                      name="paymentMethod"
                      value="upi"
                      checked={paymentMethod === 'upi'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'upi' | 'netbanking')}
                      className="fw-semibold"
                    />
                    <Form.Check
                      type="radio"
                      label={<span><i className="bi bi-bank me-2"></i>Net Banking</span>}
                      name="paymentMethod"
                      value="netbanking"
                      checked={paymentMethod === 'netbanking'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'upi' | 'netbanking')}
                      className="fw-semibold"
                    />
                  </div>
                </Form.Group>

                {/* Payment Timer */}
                {timerActive && paymentStatus === 'pending' && (
                  <Alert variant={timeLeft > 60 ? 'info' : 'warning'} className="text-center mb-4">
                    <i className="bi bi-clock-fill me-2" style={{ fontSize: '1.5rem' }}></i>
                    <h5 className="mb-2">Complete Payment Within</h5>
                    <div className="badge bg-warning text-dark fs-5">
                      <i className="bi bi-clock me-1"></i>
                      {formatTime(timeLeft)}
                    </div>
                    <p className="mt-2 mb-0">Payment session will expire after this time</p>
                  </Alert>
                )}

                {/* Payment Status */}
                {paymentStatus === 'paid' && (
                  <Alert variant="success" className="text-center mb-4">
                    <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '2rem' }}></i>
                    <h5>Payment Request Submitted Successfully!</h5>
                    <p className="mb-2">Transaction ID: <strong>{transactionId}</strong></p>
                    <p className="mb-0">Admin will verify and approve your payment. You will be notified once approved.</p>
                  </Alert>
                )}

                {paymentStatus === 'expired' && (
                  <Alert variant="danger" className="text-center mb-4">
                    <i className="bi bi-x-circle-fill me-2" style={{ fontSize: '2rem' }}></i>
                    <h5>Payment Session Expired</h5>
                    <p className="mb-2">The payment session has timed out.</p>
                    <p className="mb-0">You can save this request and complete payment later from your dashboard.</p>
                  </Alert>
                )}

                {/* UPI Payment */}
                {paymentMethod === 'upi' && paymentDetails && paymentStatus === 'pending' && (
                  <Card className="mb-3">
                    <Card.Header><h6 style={{ color: '#810081' }}>UPI Payment</h6></Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6} className="text-center mb-3">
                          <h6 style={{ color: '#810081' }}>UPI Payment QR Code</h6>
                          {qrCodeUrl ? (
                            <div className="d-flex justify-content-center mb-2">
                              <img 
                                src={qrCodeUrl} 
                                alt="UPI Payment QR Code" 
                                style={{ width: '200px', height: '200px', border: '1px solid #ddd' }}
                                onError={(e) => {
                                  console.error('QR Code failed to load');
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center border" style={{ width: '200px', height: '200px', backgroundColor: '#f8f9fa', margin: '0 auto' }}>
                              <small className="text-danger">QR Code not available</small>
                            </div>
                          )}
                          <small className="text-muted">Scan to pay with any UPI app</small>
                        </Col>
                        <Col md={6}>
                          <h6 style={{ color: '#810081' }}>Payment Details</h6>
                          <Card className="border-0" style={{ backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
                            <Card.Body>
                              <div className="mb-2">
                                <strong>Amount: ₹{selectedPackage.totalPrice}</strong>
                              </div>
                              <div className="mb-2">
                                <small><strong>UPI ID:</strong> {paymentDetails.upiId}</small>
                              </div>
                              <div className="mb-2">
                                <small><strong>Account Holder:</strong> {paymentDetails.accountHolderName || 'Natakiya'}</small>
                              </div>
                              {paymentDetails.bankName && (
                                <>
                                  <hr className="my-2" />
                                  <div className="mb-1">
                                    <small><strong>Bank:</strong> {paymentDetails.bankName}</small>
                                  </div>
                                  <div className="mb-1">
                                    <small><strong>Account:</strong> {paymentDetails.accountNumber}</small>
                                  </div>
                                  <div className="mb-1">
                                    <small><strong>IFSC:</strong> {paymentDetails.ifscCode}</small>
                                  </div>
                                </>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      
                      {/* Payment App Buttons */}
                      <div className="text-center mt-3">
                        <h6 style={{ color: '#810081' }}>Pay Now</h6>
                        <div className="d-flex justify-content-center gap-3 mb-3">
                          <Button
                            onClick={() => window.open(generateUPILink('gpay'), '_blank')}
                            className="d-flex align-items-center px-4 py-2"
                            style={{ backgroundColor: '#4285f4', border: 'none', borderRadius: '10px' }}
                          >
                            <i className="bi bi-google me-2"></i>
                            Pay with GPay
                          </Button>
                          <Button
                            onClick={() => window.open(generateUPILink('phonepe'), '_blank')}
                            className="d-flex align-items-center px-4 py-2"
                            style={{ backgroundColor: '#5f259f', border: 'none', borderRadius: '10px' }}
                          >
                            <i className="bi bi-phone me-2"></i>
                            Pay with PhonePe
                          </Button>
                        </div>
                        <div className="text-center">
                          <small className="text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            After payment, enter transaction ID below
                          </small>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Net Banking */}
                {paymentMethod === 'netbanking' && paymentDetails && paymentStatus === 'pending' && (
                  <Card className="mb-3">
                    <Card.Header><h6 style={{ color: '#810081' }}>Net Banking Details</h6></Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Card className="border-0" style={{ backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
                            <Card.Body>
                              <div className="mb-2">
                                <strong>Amount: ₹{selectedPackage.totalPrice}</strong>
                              </div>
                              <div className="mb-2">
                                <small><strong>Bank:</strong> {paymentDetails.bankName || 'Not available'}</small>
                              </div>
                              <div className="mb-2">
                                <small><strong>Account Number:</strong> {paymentDetails.accountNumber || 'Not available'}</small>
                              </div>
                              <div className="mb-2">
                                <small><strong>IFSC Code:</strong> {paymentDetails.ifscCode || 'Not available'}</small>
                              </div>
                              <div className="mb-2">
                                <small><strong>Account Holder:</strong> {paymentDetails.accountHolderName || 'Not available'}</small>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <div className="text-center">
                            <i className="bi bi-bank" style={{ fontSize: '4rem', color: '#810081' }}></i>
                            <h6 className="mt-2" style={{ color: '#810081' }}>Net Banking Transfer</h6>
                            <p className="text-muted">Use the bank details to transfer the amount via net banking</p>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* Transaction ID Input */}
                {paymentStatus === 'pending' && (
                  <Card className="border-0" style={{ backgroundColor: 'rgba(129, 0, 129, 0.05)' }}>
                    <Card.Body>
                      <h6 style={{ color: '#810081' }} className="mb-3">Payment Confirmation</h6>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Transaction ID / Reference Number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter transaction ID after completing payment"
                          value={transactionId}
                          onChange={(e) => setTransactionId(e.target.value)}
                          disabled={timeLeft === 0}
                          className="py-2"
                          style={{ borderRadius: '10px' }}
                        />
                        <Form.Text className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Complete the payment and enter the transaction ID here for verification
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button variant="outline-secondary" onClick={() => setShowPaymentModal(false)}>
              {paymentStatus === 'paid' || paymentStatus === 'expired' ? 'Close' : 'Cancel'}
            </Button>
            {paymentStatus === 'pending' && timeLeft > 0 && (
              <>
                <Button 
                  variant="outline-warning" 
                  onClick={handlePayLater}
                  disabled={loading || !selectedGroupId}
                  className="px-4"
                >
                  <i className="bi bi-clock me-2"></i>
                  {loading ? 'Saving...' : 'Pay Later'}
                </Button>
                <Button 
                  onClick={submitPayment}
                  disabled={loading || !transactionId.trim() || !selectedGroupId}
                  className="px-4"
                  style={{ backgroundColor: '#28a745', border: 'none', color: 'white' }}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  {loading ? 'Submitting...' : 'Payment Done'}
                </Button>
              </>
            )}
            {paymentStatus === 'expired' && (
              <Button 
                variant="warning" 
                onClick={handlePayLater}
                disabled={loading}
                className="px-4"
              >
                <i className="bi bi-clock me-2"></i>
                {loading ? 'Saving...' : 'Pay Later'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </PermissionWrapper>
  );
};

export default PackageSelectionPage;